import { useCallback, useEffect, useRef, useState } from "react";
import "./styles.css";

type GameState = "start" | "playing" | "crashing" | "gameover";

type ObjectType = "coin" | "cone" | "enemy";

type RoadObject = {
  id: number;
  type: ObjectType;
  lane: number;
  progress: number;
  countedAsPassed: boolean;
};

/*
  BIG, CLEAR LANE CHANGES
*/
const LANES = [14, 50, 86];

/*
  LEVEL UP EVERY 6 OBSTACLES PASSED
*/
const OBSTACLES_PER_LEVEL = 6;

export default function App() {
  const [gameState, setGameState] = useState<GameState>("start");

  const [lane, setLane] = useState(1);
  const laneRef = useRef(1);

  const [objects, setObjects] = useState<RoadObject[]>([]);

  const objectsRef = useRef<RoadObject[]>([]);

  /*
    RACE SCORE

    This comes from successfully
    passing obstacles.
  */
  const [raceScore, setRaceScore] = useState(0);

  const raceScoreRef = useRef(0);

  /*
    COINS
  */
  const [coins, setCoins] = useState(0);

  const coinsRef = useRef(0);

  /*
    OBSTACLES SUCCESSFULLY PASSED
  */
  const [obstaclesPassed, setObstaclesPassed] = useState(0);

  const obstaclesPassedRef = useRef(0);

  /*
    LEVEL
  */
  const [level, setLevel] = useState(1);

  const levelRef = useRef(1);

  const [roadSpeed, setRoadSpeed] = useState(1);

  const [bonusText, setBonusText] = useState("");

  const [bonusFlash, setBonusFlash] = useState(false);

  const [turnDirection, setTurnDirection] = useState<"left" | "right" | "">("");

  const [crashDirection, setCrashDirection] = useState<"left" | "right">(
    "right"
  );

  const previousTimeRef = useRef(0);

  const spawnTimerRef = useRef(0);

  const nextIdRef = useRef(1);

  const animationRef = useRef<number | null>(null);

  const moveLockedRef = useRef(false);

  /*
    FINAL SCORE

    Race points
    +
    coins x 10
  */
  const coinBonus = coins * 10;

  const finalScore = raceScore + coinBonus;

  /*
    START GAME
  */

  const startGame = useCallback(() => {
    laneRef.current = 1;
    setLane(1);

    objectsRef.current = [];
    setObjects([]);

    raceScoreRef.current = 0;
    setRaceScore(0);

    coinsRef.current = 0;
    setCoins(0);

    obstaclesPassedRef.current = 0;
    setObstaclesPassed(0);

    levelRef.current = 1;
    setLevel(1);

    setRoadSpeed(1);

    setBonusText("");
    setBonusFlash(false);

    setTurnDirection("");

    spawnTimerRef.current = 0;

    previousTimeRef.current = performance.now();

    setGameState("playing");
  }, []);

  /*
    LANE CHANGE
  */

  const moveLane = useCallback(
    (direction: -1 | 1) => {
      if (gameState !== "playing") {
        return;
      }

      if (moveLockedRef.current) {
        return;
      }

      const nextLane = Math.max(0, Math.min(2, laneRef.current + direction));

      if (nextLane === laneRef.current) {
        return;
      }

      moveLockedRef.current = true;

      setTurnDirection(direction === -1 ? "left" : "right");

      laneRef.current = nextLane;

      setLane(nextLane);

      window.setTimeout(() => {
        setTurnDirection("");
      }, 240);

      window.setTimeout(() => {
        moveLockedRef.current = false;
      }, 160);
    },
    [gameState]
  );

  /*
    10 COIN BONUS EFFECT

    Coins no longer directly add
    race points during gameplay.

    They are worth 10 each at
    the FINAL score.
  */

  const coinCombo = useCallback(() => {
    setBonusText("10 COINS!");

    setBonusFlash(true);

    window.setTimeout(() => {
      setBonusFlash(false);
    }, 400);

    window.setTimeout(() => {
      setBonusText("");
    }, 900);
  }, []);

  /*
    LEVEL UP
  */

  const showLevelUp = useCallback((newLevel: number) => {
    setBonusText(`LEVEL ${newLevel}!`);

    window.setTimeout(() => {
      setBonusText("");
    }, 750);
  }, []);

  /*
    CRASH
  */

  const triggerCrash = useCallback(() => {
    const direction = Math.random() < 0.5 ? "left" : "right";

    setCrashDirection(direction);

    setGameState("crashing");

    window.setTimeout(() => {
      setGameState("gameover");
    }, 950);
  }, []);

  /*
    KEYBOARD
  */

  useEffect(() => {
    function keyDown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") {
        event.preventDefault();

        moveLane(-1);
      }

      if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") {
        event.preventDefault();

        moveLane(1);
      }

      if (
        (event.key === " " || event.key === "Enter") &&
        gameState !== "playing" &&
        gameState !== "crashing"
      ) {
        event.preventDefault();

        startGame();
      }
    }

    window.addEventListener("keydown", keyDown);

    return () => {
      window.removeEventListener("keydown", keyDown);
    };
  }, [gameState, moveLane, startGame]);

  /*
    GAME LOOP
  */

  useEffect(() => {
    if (gameState !== "playing" && gameState !== "crashing") {
      return;
    }

    let running = true;

    /*
      SPAWN OBJECT
    */

    function spawnObject() {
      const roll = Math.random();

      let type: ObjectType;

      /*
        Slightly fewer coins now.

        More actual racing hazards.
      */

      if (roll < 0.25) {
        type = "coin";
      } else if (roll < 0.61) {
        type = "cone";
      } else {
        type = "enemy";
      }

      let availableLanes = [0, 1, 2];

      /*
        Don't create an impossible
        three-lane wall.
      */

      const veryRecentDanger = objectsRef.current.filter(
        (object) => object.progress < 0.18 && object.type !== "coin"
      );

      const blockedLanes = veryRecentDanger.map((object) => object.lane);

      if (type !== "coin" && blockedLanes.length >= 2) {
        availableLanes = availableLanes.filter(
          (candidate) => !blockedLanes.includes(candidate)
        );
      }

      if (availableLanes.length === 0) {
        availableLanes = [0, 1, 2];
      }

      const chosenLane =
        availableLanes[Math.floor(Math.random() * availableLanes.length)];

      const object: RoadObject = {
        id: nextIdRef.current++,

        type,

        lane: chosenLane,

        progress: 0,

        countedAsPassed: false,
      };

      objectsRef.current = [...objectsRef.current, object];
    }

    function loop(time: number) {
      if (!running) return;

      const delta = Math.min((time - previousTimeRef.current) / 1000, 0.03);

      previousTimeRef.current = time;

      const isPlaying = gameState === "playing";

      /*
        LEVEL IS NOW BASED ON
        OBSTACLES PASSED.

        0 - 5  = Level 1
        6 - 11 = Level 2
        12-17  = Level 3
        etc
      */

      if (isPlaying) {
        const newLevel =
          Math.floor(obstaclesPassedRef.current / OBSTACLES_PER_LEVEL) + 1;

        if (newLevel !== levelRef.current) {
          levelRef.current = newLevel;

          setLevel(newLevel);

          /*
            Road effects speed up too.
          */

          const visualSpeed = Math.min(1 + (newLevel - 1) * 0.18, 4);

          setRoadSpeed(visualSpeed);

          showLevelUp(newLevel);
        }

        /*
          SPAWN RATE

          Level 1 already starts
          noticeably busier.

          As levels rise, gaps shrink.
        */

        spawnTimerRef.current += delta;

        const spawnDelay = Math.max(
          0.38,

          0.88 - (levelRef.current - 1) * 0.028
        );

        if (spawnTimerRef.current >= spawnDelay) {
          spawnTimerRef.current = 0;

          spawnObject();
        }
      }

      /*
        MUCH FASTER START

        Old start:
        around 0.42

        New Level 1:
        0.62

        SPEED THEN RAMPS HARD.

        Rather than multiplying the
        whole speed by 1.5 every level,
        we're using a steep curve so
        level 10 is brutal without
        level 4 being physically
        impossible.
      */

      const levelIndex = levelRef.current - 1;

      const objectSpeed = Math.min(
        1.55,

        0.62 + levelIndex * 0.055 + Math.pow(levelIndex, 1.35) * 0.008
      );

      let crashed = false;

      const updatedObjects: RoadObject[] = [];

      for (const object of objectsRef.current) {
        /*
          CONSTANT SPEED

          Same speed from horizon,
          past NinjaCat,
          and off the bottom.

          NO slow-down.
          NO easing.
      */

        const nextProgress = object.progress + objectSpeed * delta;

        let countedAsPassed = object.countedAsPassed;

        /*
          COIN COLLECTION
        */

        if (
          isPlaying &&
          object.type === "coin" &&
          object.lane === laneRef.current &&
          nextProgress >= 0.82 &&
          nextProgress <= 0.98
        ) {
          const newCoins = coinsRef.current + 1;

          coinsRef.current = newCoins;

          setCoins(newCoins);

          if (newCoins % 10 === 0) {
            coinCombo();
          }

          continue;
        }

        /*
          CRASH
        */

        if (
          isPlaying &&
          object.type !== "coin" &&
          object.lane === laneRef.current &&
          nextProgress >= 0.84 &&
          nextProgress <= 0.97
        ) {
          crashed = true;

          continue;
        }

        /*
          SUCCESSFULLY PASSED
          AN OBSTACLE

          Once an enemy/cone has
          crossed NinjaCat without
          hitting us, count it.

          Only happens once.
      */

        if (
          isPlaying &&
          object.type !== "coin" &&
          !countedAsPassed &&
          nextProgress >= 1.0
        ) {
          countedAsPassed = true;

          const newPassed = obstaclesPassedRef.current + 1;

          obstaclesPassedRef.current = newPassed;

          setObstaclesPassed(newPassed);

          /*
            100 POINTS PER
            OBSTACLE PASSED
          */

          raceScoreRef.current += 100;

          setRaceScore(raceScoreRef.current);
        }

        /*
          OBJECT CONTINUES PAST US.

          It is NOT removed at NinjaCat.

          It goes right off the
          bottom of the screen.
      */

        if (nextProgress <= 1.32) {
          updatedObjects.push({
            ...object,

            progress: nextProgress,

            countedAsPassed,
          });
        }
      }

      objectsRef.current = updatedObjects;

      setObjects(updatedObjects);

      if (crashed && isPlaying) {
        triggerCrash();
      }

      animationRef.current = requestAnimationFrame(loop);
    }

    previousTimeRef.current = performance.now();

    animationRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;

      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, coinCombo, showLevelUp, triggerCrash]);

  /*
    OBJECT VISUAL POSITION
  */

  function getObjectStyle(object: RoadObject) {
    const progress = object.progress;

    /*
      LINEAR VERTICAL MOVEMENT

      No Math.pow here.

      They pass straight through
      at constant speed.
    */

    const top = 35 + progress * 60;

    const targetLane = LANES[object.lane];

    /*
      Perspective only affects
      sideways lane spread.
    */

    const spread = Math.min(1, 0.11 + progress * 1.08);

    const left = 50 + (targetLane - 50) * spread;

    /*
      Perspective SIZE does not
      affect travel speed.
    */

    const sizeProgress = Math.pow(Math.min(progress, 1.12), 1.18);

    let finalWidth = 110;

    if (object.type === "coin") {
      finalWidth = 92;
    }

    if (object.type === "cone") {
      finalWidth = 112;
    }

    if (object.type === "enemy") {
      finalWidth = 160;
    }

    const width = finalWidth * (0.12 + sizeProgress);

    return {
      left: `${left}%`,

      top: `${top}%`,

      width: `${width}px`,

      zIndex: 15 + Math.floor(progress * 45),

      opacity: Math.min(1, 0.55 + progress * 1.2),
    };
  }

  return (
    <div className="page">
      <div
        className={`
          game

          level-${Math.min(level, 7)}

          ${bonusFlash ? "bonus-flash" : ""}

          ${gameState === "crashing" ? "crash-active" : ""}
        `}
        style={
          {
            "--road-speed": `${0.42 / roadSpeed}s`,

            "--town-speed": `${3 / roadSpeed}s`,
          } as React.CSSProperties
        }
      >
        <div className="town-layer" />

        <div className="sun-wash" />

        {(gameState === "playing" || gameState === "crashing") && (
          <>
            <div className="road-rush" />

            <div className="speed-edge left-edge" />

            <div className="speed-edge right-edge" />

            <div className="speed-vignette" />
          </>
        )}

        {/* HUD */}

        {(gameState === "playing" || gameState === "crashing") && (
          <div className="hud">
            <div className="hud-box score-box">
              <span>SCORE</span>

              <strong>{raceScore}</strong>
            </div>

            <div className="hud-box level-box">
              <span>LEVEL</span>

              <strong>{level}</strong>
            </div>

            <div className="hud-box coin-box">
              <img src="/coin.png" alt="" />

              <strong>{coins}</strong>
            </div>
          </div>
        )}

        {/* ROAD OBJECTS */}

        {(gameState === "playing" || gameState === "crashing") &&
          objects.map((object) => (
            <img
              key={object.id}
              className={`road-object ${object.type}`}
              src={
                object.type === "coin"
                  ? "/coin.png"
                  : object.type === "cone"
                  ? "/cone.png"
                  : "/enemy.png"
              }
              alt=""
              draggable={false}
              style={getObjectStyle(object)}
            />
          ))}

        {/* NINJACAT */}

        {(gameState === "playing" || gameState === "crashing") && (
          <div
            className={`
              player-wrapper

              ${
                gameState === "playing" && turnDirection === "left"
                  ? "turn-left"
                  : ""
              }

              ${
                gameState === "playing" && turnDirection === "right"
                  ? "turn-right"
                  : ""
              }

              ${
                gameState === "crashing"
                  ? crashDirection === "left"
                    ? "crash-left"
                    : "crash-right"
                  : ""
              }
            `}
            style={{
              left: `${LANES[lane]}%`,
            }}
          >
            <div className="boost-trail" />

            <img
              className="player"
              src="/NinjaCat.png"
              alt="NinjaCat"
              draggable={false}
            />
          </div>
        )}

        {/* CRASH EFFECT */}

        {gameState === "crashing" && (
          <>
            <div className="crash-flash" />

            <div className="crash-bang">💥</div>
          </>
        )}

        {/* LEVEL / COIN TEXT */}

        {bonusText && (
          <div
            className={`bonus-message ${
              bonusText.includes("COINS") ? "manyu-bonus" : ""
            }`}
          >
            {bonusText}
          </div>
        )}

        {/* CONTROLS */}

        {gameState === "playing" && (
          <div className="controls">
            <button
              className={`lane-button ${lane === 0 ? "inactive" : ""}`}
              onPointerDown={(event) => {
                event.preventDefault();

                moveLane(-1);
              }}
            >
              ◀
            </button>

            <div className="lane-dots">
              <span className={lane === 0 ? "selected" : ""} />

              <span className={lane === 1 ? "selected" : ""} />

              <span className={lane === 2 ? "selected" : ""} />
            </div>

            <button
              className={`lane-button ${lane === 2 ? "inactive" : ""}`}
              onPointerDown={(event) => {
                event.preventDefault();

                moveLane(1);
              }}
            >
              ▶
            </button>
          </div>
        )}

        {/* START */}

        {gameState === "start" && (
          <div className="overlay start-screen">
            <div className="mini-title">MANYU ARCADE</div>

            <h1>
              NINJACAT
              <span>RACING</span>
            </h1>

            <img className="start-kart" src="/NinjaCat.png" alt="" />

            <div className="instructions">
              CHANGE LANES • DODGE • COLLECT COINS
            </div>

            <button className="play-button" onClick={startGame}>
              START RACE
            </button>

            <div className="control-hint">◀ CHANGE LANES ▶</div>
          </div>
        )}

        {/* GAME OVER */}

        {gameState === "gameover" && (
          <div className="overlay gameover-screen">
            <div className="crash">CRASH!</div>

            <h2>RACE OVER</h2>

            <div className="results">
              <div>
                <span>RACE</span>

                <strong>{raceScore}</strong>
              </div>

              <div>
                <span>LEVEL</span>

                <strong>{level}</strong>
              </div>
            </div>

            <div className="coins-result">
              <img src="/coin.png" alt="" />

              <strong>{coins}</strong>

              <span>COINS × 10</span>

              <strong>+{coinBonus}</strong>
            </div>

            <div
              style={{
                marginBottom: "20px",
                padding: "10px 20px",
                borderRadius: "14px",
                background: "rgba(4, 23, 49, 0.88)",
                border: "2px solid white",
                color: "white",
                fontWeight: 1000,
                fontSize: "14px",
                letterSpacing: "1px",
              }}
            >
              FINAL SCORE
              <div
                style={{
                  color: "#ffe145",
                  fontSize: "38px",
                  lineHeight: "42px",
                  marginTop: "3px",
                }}
              >
                {finalScore}
              </div>
            </div>

            <button className="play-button" onClick={startGame}>
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
